import { Button, Flex, Tooltip, Typography } from "antd";
import { Stack, Toggle } from "@libertify/ui";
import { ArrowClockwiseIcon } from "@phosphor-icons/react";
import { useTranslate } from "@refinedev/core";
import { PaletteResponse } from "pages/media/types";
import { Table, TableDividerStyle } from "../../../../types";
import { ItemAlignmentFeature } from "../../../features/ItemAlignmentFeature";
import { IconColorPicker } from "../../../controls/IconColorPicker";
import { CommitSlider } from "../../../controls/CommitSlider";
import { ItemControlsConfigProvider } from "../../../controls/ItemControlsConfigProvider";

/**
 * Adjusts position so the divider stays within the cell when its thickness changes.
 * - If the divider is near the end edge (bottom/right), it grows inward.
 * - If it is near the start edge (top/left), it stays pinned.
 * - Otherwise it stays centered around its current midpoint.
 */
const preserveAlignmentOnResize = (
  oldPos: number,
  oldThickness: number,
  newThickness: number
): number => {
  const endEdge = oldPos + oldThickness;

  let newPos: number;
  if (endEdge > 0.95) {
    // Pinned to end — grow inward
    newPos = endEdge - newThickness;
  } else if (oldPos < 0.05) {
    // Pinned to start — keep position
    newPos = oldPos;
  } else {
    // Centered — preserve midpoint
    const center = oldPos + oldThickness / 2;
    newPos = center - newThickness / 2;
  }

  return Math.max(0, Math.min(newPos, 1 - newThickness));
};

export const TableDividerStyleFeature = ({
  table,
  palette,
  previewWidth,
  previewHeight,
  onUpdateTable,
  onChange,
}: {
  table: Table;
  palette?: PaletteResponse;
  previewWidth: number;
  previewHeight: number;
  onUpdateTable: (update: Partial<Table>) => void;
  onChange: (style: Partial<TableDividerStyle>) => void;
}) => {
  const t = useTranslate();

  const handleDividerThicknessChange = (percentage: number) => {
    const { dividerStyle } = table;
    const isHorizontal = dividerStyle.orientation === "horizontal";
    const newThickness = percentage / 100;
    const oldThickness = isHorizontal
      ? dividerStyle.size.height
      : dividerStyle.size.width;
    const posKey = isHorizontal ? "y" : "x";
    const oldPos = dividerStyle.position[posKey];

    const newPos = preserveAlignmentOnResize(
      oldPos,
      oldThickness,
      newThickness
    );

    onChange({
      size: isHorizontal
        ? { ...dividerStyle.size, height: newThickness }
        : { ...dividerStyle.size, width: newThickness },
      position: { ...dividerStyle.position, [posKey]: newPos },
    });
  };

  const handleDividerLengthChange = (percentage: number) => {
    const { dividerStyle } = table;
    const isHorizontal = dividerStyle.orientation === "horizontal";
    const newLength = percentage / 100;
    const oldLength = isHorizontal
      ? dividerStyle.size.width
      : dividerStyle.size.height;
    const posKey = isHorizontal ? "x" : "y";
    const oldPos = dividerStyle.position[posKey];

    const newPos = preserveAlignmentOnResize(oldPos, oldLength, newLength);

    onChange({
      size: isHorizontal
        ? { ...dividerStyle.size, width: newLength }
        : { ...dividerStyle.size, height: newLength },
      position: { ...dividerStyle.position, [posKey]: newPos },
    });
  };

  const handleDividerRotate = () => {
    const { dividerStyle } = table;
    const newOrientation =
      dividerStyle.orientation === "horizontal" ? "vertical" : "horizontal";

    onChange({
      orientation: newOrientation,
      size: {
        width: dividerStyle.size.height,
        height: dividerStyle.size.width,
      },
      position: {
        x: dividerStyle.position.y,
        y: dividerStyle.position.x,
      },
    });
  };

  return (
    <>
      <Stack align="center" gap="sm">
        <Toggle
          checked={table.showDividers}
          onChange={(checked) => onUpdateTable({ showDividers: checked })}
        />
        <Typography.Text>
          {t("components.templates.settings.tableTab.showDividers")}
        </Typography.Text>
      </Stack>
      {table.showDividers && (
        <ItemControlsConfigProvider>
          <Flex vertical gap={12}>
            <Flex align="center" gap={8}>
              {palette && (
                <IconColorPicker
                  value={table.dividerStyle.background}
                  allowClear={false}
                  onChangeComplete={(resolvedColor) => {
                    onChange({ background: resolvedColor });
                  }}
                  title={t(
                    "components.templates.settings.tableTab.dividerColor"
                  )}
                  palette={palette}
                  type="background"
                  hint={t(
                    "components.templates.settings.tableTab.dividerColor"
                  )}
                />
              )}
              <Tooltip
                title={t(
                  "components.templates.settings.tableTab.dividerRotate"
                )}
              >
                <Button
                  type="text"
                  icon={<ArrowClockwiseIcon size={20} />}
                  onClick={handleDividerRotate}
                />
              </Tooltip>
            </Flex>
            <ItemAlignmentFeature
              pickedItem={table.dividerStyle}
              onChange={(value) => {
                const { position, size } = value as {
                  position?: { x: number; y: number };
                  size?: { width: number; height: number };
                };
                onChange({
                  ...(position ? { position } : {}),
                  ...(size ? { size } : {}),
                });
              }}
              previewWidth={previewWidth}
              previewHeight={previewHeight}
            />
            <Flex vertical>
              <CommitSlider
                min={1}
                max={100}
                step={1}
                value={Math.round(
                  (table.dividerStyle.orientation === "horizontal"
                    ? table.dividerStyle.size.width
                    : table.dividerStyle.size.height) * 100
                )}
                onCommit={handleDividerLengthChange}
                tooltip={{ formatter: (value) => `${value}%` }}
              />
              <Typography.Text style={{ fontSize: 12 }}>
                {t("components.templates.settings.tableTab.dividerLength")}
              </Typography.Text>
            </Flex>
            <Flex vertical>
              <CommitSlider
                min={1}
                max={100}
                step={1}
                value={Math.round(
                  (table.dividerStyle.orientation === "horizontal"
                    ? table.dividerStyle.size.height
                    : table.dividerStyle.size.width) * 100
                )}
                onCommit={handleDividerThicknessChange}
                tooltip={{ formatter: (value) => `${value}%` }}
              />
              <Typography.Text style={{ fontSize: 12 }}>
                {t("components.templates.settings.tableTab.dividerThickness")}
              </Typography.Text>
            </Flex>
          </Flex>
        </ItemControlsConfigProvider>
      )}
    </>
  );
};
